import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  applyUserOverride,
  parseOverride,
  USER_OVERRIDE_KEY,
  type UserThemeOverride,
} from '../../theme/contract'

/*
 * ThemeProvider decides WHICH theme is active. It does not decide what a
 * theme looks like — every value lives in src/theme.css.
 *
 * That means this file writes no colours, no font stacks and no sizes. It
 * sets one class plus a few data-attributes on <html>, and theme.css does
 * the rest:
 *
 *   theme        -> html class          .light .dark .diary .diary-dark
 *   accent       -> html[data-accent]
 *   UI font      -> html[data-ui-font]
 *   handwriting  -> html[data-hand-font]
 *   body voice   -> html[data-body-font]
 *
 * The single exception is the Theme Builder, where the user picks arbitrary
 * colours that cannot be enumerated in CSS ahead of time. Those go into one
 * <style id="user-theme"> element (see applyUserTheme below) and are the
 * ONLY style values this app writes at runtime.
 *
 * The font tables below hold var() REFERENCES rather than literal stacks, so
 * a preview still renders in the right face while theme.css remains the only
 * place a font name is written down.
 */

// Theme includes both modern themes and Dear Diary theme variants
export type Theme =
  | 'light' | 'dark' | 'system' | 'diary' | 'diary-dark'
  | 'sand' | 'sage' | 'blush' | 'contrast'
  | 'forest' | 'ocean' | 'nocturne'
  | 'ember' | 'plum'
type ThemeStyle = 'modern' | 'diary'

export interface ThemeMeta {
  id: Exclude<Theme, 'system'>
  /** Name shown in the picker. */
  label: string
  /** One line of why you'd pick it. */
  hint: string
  /** Whether components that branch in JS should treat this as dark. */
  dark: boolean
  /** Whether the journal chrome (handwriting, ribbons, chapters) is on. */
  diary: boolean
  /**
   * Where the dark/light toggle goes from here. Set only where a genuine
   * counterpart exists — Sage and Forest are the same green at two times of
   * day, so toggling between them keeps your choice. Ocean is the one
   * leftover without a twin: it falls back to Daylight rather than
   * pretending Sage is its pair.
   */
  twin?: Exclude<Theme, 'system'>
}

/**
 * Every theme, in picker order. The class on <html> is always the id, which is
 * what `html.<id>` blocks in theme.css key off.
 *
 * This is a table rather than a switch because the theme was previously
 * described in four places — a type union, a switch, the classList.remove list
 * and the picker — and adding one meant editing all four. Forgetting the
 * remove list was the interesting failure: the old class stayed on <html>, so
 * two themes' tokens applied at once and the winner came down to source order.
 *
 * Adding a theme is now: a block in theme.css, and one row here.
 */
export const THEMES: ThemeMeta[] = [
  { id: 'light',      label: 'Daylight',  hint: 'cool and bright',        dark: false, diary: false, twin: 'dark' },
  { id: 'sand',       label: 'Sand',      hint: 'warm paper daylight',    dark: false, diary: false, twin: 'ember' },
  { id: 'sage',       label: 'Sage',      hint: 'soft green daylight',    dark: false, diary: false, twin: 'forest' },
  { id: 'blush',      label: 'Blush',     hint: 'soft rose daylight',     dark: false, diary: false, twin: 'plum' },
  { id: 'contrast',   label: 'Contrast',  hint: 'maximum legibility',     dark: false, diary: false, twin: 'nocturne' },
  { id: 'dark',       label: 'Midnight',  hint: 'the classic dark',       dark: true,  diary: false, twin: 'light' },
  { id: 'forest',     label: 'Forest',    hint: 'deep green night',       dark: true,  diary: false, twin: 'sage' },
  { id: 'ocean',      label: 'Ocean',     hint: 'deep teal night',        dark: true,  diary: false },
  { id: 'nocturne',   label: 'Nocturne',  hint: 'true black, kind to OLED', dark: true, diary: false, twin: 'contrast' },
  { id: 'ember',      label: 'Ember',     hint: 'warm brown night',       dark: true,  diary: false, twin: 'sand' },
  { id: 'plum',       label: 'Plum',      hint: 'rose night',             dark: true,  diary: false, twin: 'blush' },
  { id: 'diary',      label: 'Journal',   hint: 'paper, ink and ribbons', dark: false, diary: true,  twin: 'diary-dark' },
  { id: 'diary-dark', label: 'Journal night', hint: 'the same book, by lamplight', dark: true, diary: true, twin: 'diary' },
]

const THEME_CLASSES = THEMES.map((t) => t.id)

// Available handwritten font styles for Dear Diary theme
// Cross-platform compatibility: Web, Android, iOS, TV, Tablets
export type DiaryFontId =
  | 'kalam' | 'patrick-hand' | 'caveat' | 'dancing-script'
  | 'gochi-hand' | 'indie-flower' | 'architects-daughter'

export interface DiaryFont {
  id: DiaryFontId
  name: string
  fontFamily: string
  description: string
  weights: number[]        // Available font weights
  recommended: boolean     // Cross-platform recommended
  platformNotes: string    // Compatibility notes
}

export const DIARY_FONTS: DiaryFont[] = [
  {
    id: 'kalam',
    name: 'Kalam',
    fontFamily: 'var(--p-font-kalam)',
    description: 'Authentic ballpoint pen',
    weights: [300, 400, 700],
    recommended: true,
    platformNotes: '✓ Best for all platforms - readable on TV, mobile & tablets'
  },
  {
    id: 'patrick-hand',
    name: 'Patrick Hand',
    fontFamily: 'var(--p-font-patrick)',
    description: 'Clean hand-drawn',
    weights: [400],
    recommended: true,
    platformNotes: '✓ Very readable at all sizes - great for TV apps'
  },
  {
    id: 'caveat',
    name: 'Caveat',
    fontFamily: 'var(--p-font-caveat)',
    description: 'Casual quick writing',
    weights: [400, 500, 600, 700],
    recommended: true,
    platformNotes: '✓ Multiple weights - flexible for all screen sizes'
  },
  {
    id: 'dancing-script',
    name: 'Dancing Script',
    fontFamily: 'var(--p-font-dancing)',
    description: 'Elegant calligraphy',
    weights: [400, 500, 600, 700],
    recommended: false,
    platformNotes: 'Best for headings - may be less readable at small sizes'
  },
  {
    id: 'gochi-hand',
    name: 'Gochi Hand',
    fontFamily: 'var(--p-font-gochi)',
    description: 'Bold felt-tip pen',
    weights: [400],
    recommended: true,
    platformNotes: '\u2713 Strong strokes - reads well on light AND dark paper',
  },
  {
    id: 'indie-flower',
    name: 'Indie Flower',
    fontFamily: 'var(--p-font-indie)',
    description: 'Round and bubbly',
    weights: [400],
    recommended: true,
    platformNotes: '\u2713 The cheeriest of the set - very legible',
  },
  {
    id: 'architects-daughter',
    name: 'Architects Daughter',
    fontFamily: 'var(--p-font-architects)',
    description: 'Neat drafting print',
    weights: [400],
    recommended: true,
    platformNotes: '\u2713 Wide letterforms - watch long titles on phones',
  },
]

// Default: Gochi Hand — strong strokes, reads on light and dark paper
export const DEFAULT_DIARY_FONT: DiaryFontId = 'gochi-hand'

// Custom Diary Theme Configuration (from Theme Builder)
export interface DiaryThemeConfig {
  paper: {
    type: 'solid' | 'gradient' | 'texture'
    solidColor: string
    gradientFrom: string
    gradientTo: string
    gradientDirection: string
    textureId: string
    patternId: string
    patternOpacity: number
  }
  fonts: {
    handwritten: string
    body: string
    ui: string
  }
  light: DiaryThemeColors
  dark: DiaryThemeColors
}

export interface DiaryThemeColors {
  paperPrimary: string
  paperSecondary: string
  paperMargin: string
  inkPrimary: string
  inkSecondary: string
  inkFaded: string
  accentPrimary: string
  accentSecondary: string
  bookmark: string
  highlight: string
  linkDefault: string
  linkHover: string
  borderLight: string
  borderMedium: string
  shadowColor: string
}

interface ThemeContextType {
  theme: Theme
  isDark: boolean
  isDiary: boolean
  themeStyle: ThemeStyle
  diaryFont: DiaryFontId
  diaryFontFamily: string
  diaryThemeConfig: DiaryThemeConfig | null
  accentColor: AccentColorId
  uiFont: UIFontId
  yanamasButtonStyle: YanamasButtonStyle
  yanamasCardStyle: YanamasCardStyle
  yanamasUiFont: UIFontId
  yanamasBodyFont: BodyFontId
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  toggleDarkMode: () => void
  setThemeStyle: (style: ThemeStyle) => void
  setDiaryFont: (font: DiaryFontId) => void
  setAccentColor: (color: AccentColorId) => void
  setUIFont: (font: UIFontId) => void
  setYanamasButtonStyle: (style: YanamasButtonStyle) => void
  setYanamasCardStyle: (style: YanamasCardStyle) => void
  setYanamasUiFont: (font: UIFontId) => void
  setYanamasBodyFont: (font: BodyFontId) => void
  reloadDiaryTheme: () => void
  userOverride: UserThemeOverride
  setUserOverrideToken: (id: keyof UserThemeOverride, value: string | null) => void
  resetUserOverride: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

// Accent presets — yanamas Purple is the locked default for modern mode.
// The ramps themselves live in theme.css under [data-accent="…"]; a picker
// previews one by putting that attribute on the swatch element and letting
// .accent-swatch read var(--accent-500).
export type AccentColorId = 'horton' | 'indigo' | 'purple' | 'blue' | 'green' | 'rose' | 'amber' | 'teal' | 'orange'

export interface AccentColorDef {
  id: AccentColorId
  name: string
}

export const ACCENT_COLORS: AccentColorDef[] = [
  { id: 'horton', name: 'Brand purple' },
  { id: 'indigo', name: 'Indigo' },
  { id: 'purple', name: 'Purple' },
  { id: 'blue', name: 'Blue' },
  { id: 'green', name: 'Green' },
  { id: 'rose', name: 'Rose' },
  { id: 'amber', name: 'Amber' },
  { id: 'teal', name: 'Teal' },
  { id: 'orange', name: 'Orange' },
]

export const DEFAULT_ACCENT: AccentColorId = 'horton'

// UI font presets
export type UIFontId = 'inter' | 'nunito' | 'dm-sans' | 'plus-jakarta' | 'quicksand' | 'comfortaa' | 'baloo-2'

export interface UIFontDef {
  id: UIFontId
  name: string
  fontFamily: string
}

export const UI_FONTS: UIFontDef[] = [
  { id: 'inter', name: 'Inter', fontFamily: 'var(--p-font-inter)' },
  { id: 'nunito', name: 'Nunito', fontFamily: 'var(--p-font-nunito)' },
  { id: 'dm-sans', name: 'DM Sans', fontFamily: 'var(--p-font-dm-sans)' },
  { id: 'plus-jakarta', name: 'Plus Jakarta Sans', fontFamily: 'var(--p-font-jakarta)' },
  { id: 'quicksand', name: 'Quicksand', fontFamily: 'var(--p-font-quicksand)' },
  { id: 'comfortaa', name: 'Comfortaa', fontFamily: 'var(--p-font-comfortaa)' },
  { id: 'baloo-2', name: 'Baloo 2', fontFamily: 'var(--p-font-baloo)' },
]

export const DEFAULT_UI_FONT: UIFontId = 'inter'

// ═══════════════════════════════════════════════════════════════════════════
// Yanamas customization — the axes an end user can restyle in diary mode.
// Owner picks (2026-08-22 theme-lab review) are the DEFAULTS: Gochi Hand
// handwriting, Quicksand UI, hand-drawn "sketch" buttons, index-card cards.
// Everything persists per-device in localStorage like the other diary prefs.
// ═══════════════════════════════════════════════════════════════════════════

export type YanamasButtonStyle = 'sketch' | 'wax' | 'ink' | 'tape'
export type YanamasCardStyle = 'index' | 'plain' | 'ruled' | 'taped' | 'folded'

export interface YanamasStyleOption<T extends string> {
  id: T
  name: string
  description: string
}

export const YANAMAS_BUTTON_STYLES: YanamasStyleOption<YanamasButtonStyle>[] = [
  { id: 'sketch', name: 'Hand-drawn', description: 'Wonky pen-drawn box, handwritten label' },
  { id: 'wax', name: 'Wax seal', description: 'Solid purple, presses down' },
  { id: 'ink', name: 'Ink block', description: 'Quiet sepia ink on paper' },
  { id: 'tape', name: 'Washi tape', description: 'A tape strip, slightly askew' },
]

export const YANAMAS_CARD_STYLES: YanamasStyleOption<YanamasCardStyle>[] = [
  { id: 'index', name: 'Index card', description: 'Red margin line down the left' },
  { id: 'plain', name: 'Plain paper', description: 'Simple sheet with a sepia edge' },
  { id: 'ruled', name: 'Ruled page', description: 'Notebook lines behind the text' },
  { id: 'taped', name: 'Taped in', description: 'Held by a strip of tape' },
  { id: 'folded', name: 'Folded corner', description: 'A dog-eared page corner' },
]

export const DEFAULT_YANAMAS_BUTTON: YanamasButtonStyle = 'sketch'
export const DEFAULT_YANAMAS_CARD: YanamasCardStyle = 'index'
/** Diary mode's own UI-sans default (modern mode keeps DEFAULT_UI_FONT). */
export const DEFAULT_YANAMAS_UI_FONT: UIFontId = 'quicksand'

// Body/serif voice — quiet evidence lines, meta text, acknowledgements.
export type BodyFontId = 'match-ui' | 'lora' | 'crimson-pro' | 'eb-garamond'
export interface BodyFontDef { id: BodyFontId; name: string; fontFamily: string }
export const BODY_FONTS: BodyFontDef[] = [
  // 'match-ui' resolves to the picked UI sans — ONE font across all content,
  // which is the yanamas default. The serifs are the opt-in "bookish" voice.
  { id: 'match-ui', name: 'Same as buttons', fontFamily: 'var(--font-ui)' },
  { id: 'lora', name: 'Lora', fontFamily: 'var(--p-font-lora)' },
  { id: 'crimson-pro', name: 'Crimson Pro', fontFamily: 'var(--p-font-crimson)' },
  { id: 'eb-garamond', name: 'EB Garamond', fontFamily: 'var(--p-font-garamond)' },
]
export const DEFAULT_YANAMAS_BODY_FONT: BodyFontId = 'match-ui'

// Derived from the table so a theme can never be selectable in the picker yet
// rejected on reload — which is what a hand-maintained copy of this list did.
const validThemes: Theme[] = [...THEMES.map((t) => t.id), 'system']
const validFonts: DiaryFontId[] = ['kalam', 'patrick-hand', 'caveat', 'dancing-script', 'gochi-hand', 'indie-flower', 'architects-daughter']

const USER_THEME_STYLE_ID = 'user-theme'

/**
 * The one place this app writes style values at runtime.
 *
 * A Theme Builder config holds freely-picked colours, so it cannot be
 * enumerated in theme.css. Rather than scatter setProperty calls across the
 * root element, serialise the whole thing into a single <style> element that
 * can be inspected, and removed, in one piece.
 *
 * Selectors use :root.diary rather than html.diary so they outrank the
 * stylesheet defaults regardless of injection order.
 */
function applyUserTheme(config: DiaryThemeConfig | null): void {
  const existing = document.getElementById(USER_THEME_STYLE_ID)

  if (!config) {
    // No custom theme: remove the override entirely and let theme.css win.
    existing?.remove()
    return
  }

  const block = (selector: string, colors: DiaryThemeColors): string => `
${selector} {
  --paper-cream: ${colors.paperPrimary};
  --paper-aged: ${colors.paperSecondary};
  --paper-margin: ${colors.paperMargin};
  --ink-black: ${colors.inkPrimary};
  --ink-sepia: ${colors.inkSecondary};
  --ink-faded: ${colors.inkFaded};
  --horton-purple: ${colors.accentPrimary};
  --horton-blue: ${colors.accentSecondary};
  --bookmark-red: ${colors.bookmark};
  --highlight-yellow: ${colors.highlight};
  --link-default: ${colors.linkDefault};
  --link-hover: ${colors.linkHover};
  --border-light: ${colors.borderLight};
  --border-medium: ${colors.borderMedium};
  --shadow-color: ${colors.shadowColor};
}`

  let css = block(':root.diary', config.light) + block(':root.diary-dark', config.dark)

  if (config.paper) {
    const background = config.paper.type === 'gradient'
      ? `linear-gradient(${config.paper.gradientDirection}, ${config.paper.gradientFrom}, ${config.paper.gradientTo})`
      : (config.paper.solidColor || config.light.paperPrimary)
    css += `
:root.diary, :root.diary-dark {
  --diary-bg: ${background};
  --paper-texture-id: ${config.paper.textureId};
  --paper-pattern-id: ${config.paper.patternId};
  --paper-pattern-opacity: ${config.paper.patternOpacity};
}`
  }

  const el = existing ?? Object.assign(document.createElement('style'), { id: USER_THEME_STYLE_ID })
  el.textContent = css
  if (!existing) document.head.appendChild(el)
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'horton-theme'
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey)
      if (stored && validThemes.includes(stored as Theme)) {
        return stored as Theme
      }
    }
    return defaultTheme
  })

  const [diaryFont, setDiaryFontState] = useState<DiaryFontId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('horton-diary-font')
      if (stored && validFonts.includes(stored as DiaryFontId)) {
        return stored as DiaryFontId
      }
    }
    return DEFAULT_DIARY_FONT
  })

  const [accentColor, setAccentColorState] = useState<AccentColorId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('horton-accent-color')
      if (stored && ACCENT_COLORS.some(c => c.id === stored)) return stored as AccentColorId
    }
    return DEFAULT_ACCENT
  })

  const [uiFont, setUIFontState] = useState<UIFontId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('horton-ui-font')
      if (stored && UI_FONTS.some(f => f.id === stored)) return stored as UIFontId
    }
    return DEFAULT_UI_FONT
  })

  // Yanamas customization state (diary mode only; per-device)
  const [yanamasButtonStyle, setYanamasButtonStyleState] = useState<YanamasButtonStyle>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('yanamas-button-style')
      if (stored && YANAMAS_BUTTON_STYLES.some(o => o.id === stored)) return stored as YanamasButtonStyle
    }
    return DEFAULT_YANAMAS_BUTTON
  })
  const [yanamasCardStyle, setYanamasCardStyleState] = useState<YanamasCardStyle>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('yanamas-card-style')
      if (stored && YANAMAS_CARD_STYLES.some(o => o.id === stored)) return stored as YanamasCardStyle
    }
    return DEFAULT_YANAMAS_CARD
  })
  const [yanamasUiFont, setYanamasUiFontState] = useState<UIFontId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('yanamas-ui-font')
      if (stored && UI_FONTS.some(f => f.id === stored)) return stored as UIFontId
    }
    return DEFAULT_YANAMAS_UI_FONT
  })
  const [yanamasBodyFont, setYanamasBodyFontState] = useState<BodyFontId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('yanamas-body-font')
      if (stored && BODY_FONTS.some(f => f.id === stored)) return stored as BodyFontId
    }
    return DEFAULT_YANAMAS_BODY_FONT
  })

  // Load custom diary theme from Theme Builder
  const [diaryThemeConfig, setDiaryThemeConfig] = useState<DiaryThemeConfig | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('horton-active-diary-theme')
      if (stored) {
        try {
          return JSON.parse(stored) as DiaryThemeConfig
        } catch {
          return null
        }
      }
    }
    return null
  })

  const [userOverride, setUserOverrideState] = useState<UserThemeOverride>(() =>
    typeof window !== 'undefined' ? parseOverride(localStorage.getItem(USER_OVERRIDE_KEY)) : {},
  )

  const [isDark, setIsDark] = useState(false)
  const [isDiary, setIsDiary] = useState(false)
  const [themeStyle, setThemeStyleState] = useState<ThemeStyle>('modern')

  // Get the font family reference for the current diary font
  const diaryFontFamily = DIARY_FONTS.find(f => f.id === diaryFont)?.fontFamily || 'var(--p-font-kalam)'

  // Reload diary theme from localStorage (called after Theme Builder saves)
  const reloadDiaryTheme = useCallback(() => {
    const stored = localStorage.getItem('horton-active-diary-theme')
    if (stored) {
      try {
        setDiaryThemeConfig(JSON.parse(stored))
      } catch {
        setDiaryThemeConfig(null)
      }
    }
  }, [])

  // ── Which theme is active: one class on <html> ──────────────────────────
  useEffect(() => {
    const root = window.document.documentElement

    // Removes EVERY theme class, not a hand-listed subset, so a theme added to
    // the table above can never leave a stale class behind.
    root.classList.remove(...THEME_CLASSES)

    // 'system' resolves to a real theme; anything unrecognised (a stale value
    // in localStorage from a renamed theme) falls back rather than leaving
    // <html> with no theme class at all.
    const resolvedId =
      theme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        : theme
    const meta = THEMES.find((t) => t.id === resolvedId) ?? THEMES[0]

    root.classList.add(meta.id)
    setIsDark(meta.dark)
    setIsDiary(meta.diary)
    setThemeStyleState(meta.diary ? 'diary' : 'modern')

    // Listen for system theme changes when using 'system' theme
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        root.classList.remove(...THEME_CLASSES)
        root.classList.add(e.matches ? 'dark' : 'light')
        setIsDark(e.matches)
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  // ── Keep the browser chrome in step with the active theme.
  //    theme.css declares --meta-theme-color per theme; index.html ships a
  //    static value that would otherwise never update on a theme switch. ───
  useEffect(() => {
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--meta-theme-color')
      .trim()
    if (!color) return
    document.querySelectorAll('meta[name="theme-color"]')
      .forEach(tag => tag.setAttribute('content', color))
  }, [theme, isDark, isDiary])

  // ── Preset selection. Each of these is an enumerable choice, so CSS holds
  //    every option and we only name the one that is active. ──────────────
  useEffect(() => {
    document.documentElement.dataset.handFont = diaryFont
  }, [diaryFont])

  // Modern mode is locked to yanamas Purple: with no data-accent present the
  // :root ramp in theme.css applies, which is exactly the Horton purple.
  useEffect(() => {
    const root = window.document.documentElement
    const inDiary = theme === 'diary' || theme === 'diary-dark'
    if (inDiary) {
      root.dataset.accent = accentColor
    } else {
      delete root.dataset.accent
    }
  }, [accentColor, theme])

  // Diary mode has its own UI-sans track (yanamasUiFont, default Quicksand)
  // so restyling the diary never touches the modern shell's Inter.
  useEffect(() => {
    const root = window.document.documentElement
    const inDiary = theme === 'diary' || theme === 'diary-dark'
    root.dataset.uiFont = inDiary ? yanamasUiFont : uiFont
    if (inDiary) {
      root.dataset.bodyFont = yanamasBodyFont
    } else {
      // Leaving diary: drop the override so the @theme serif default returns.
      delete root.dataset.bodyFont
    }
  }, [uiFont, yanamasUiFont, yanamasBodyFont, theme])

  // ── The one runtime style write: a Theme Builder config cannot be
  //    enumerated in CSS, so it becomes a single <style> element. ──────────
  useEffect(() => {
    applyUserTheme(diaryThemeConfig)
  }, [diaryThemeConfig])

  // Per-token overrides from the Theme Builder. Applied after the diary
  // config so a user who retunes --bg-surface while using Journal still wins.
  useEffect(() => {
    applyUserOverride(userOverride)
  }, [userOverride])

  const setTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme)
    setThemeState(newTheme)
  }, [storageKey])

  // Step to the next theme in picker order, wrapping at the end.
  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      const at = THEMES.findIndex((t) => t.id === prev)
      // From 'system' (or anything unknown) start at whichever end matches the
      // OS, rather than jumping to the top of the list.
      const newTheme: Theme =
        at === -1
          ? window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
          : THEMES[(at + 1) % THEMES.length].id

      localStorage.setItem(storageKey, newTheme)
      return newTheme
    })
  }, [storageKey])

  // Toggle just dark/light, staying in the same family where one exists.
  const toggleDarkMode = useCallback(() => {
    setThemeState(prev => {
      const meta = THEMES.find((t) => t.id === prev)
      const newTheme: Theme =
        meta?.twin ?? (meta?.dark ? 'light' : 'dark')
      localStorage.setItem(storageKey, newTheme)
      // Keep the server preference in agreement so other devices follow
      // and the profile page reads the truth (fire-and-forget; the local
      // choice already won — see useThemeSync).
      if (newTheme === 'dark' || newTheme === 'light') {
        void fetch('/api/profile/app-preferences', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme_preference: newTheme }),
        }).catch(() => undefined)
      }
      return newTheme
    })
  }, [storageKey])

  // Switch between modern and diary styles, keeping the time of day.
  const setThemeStyle = useCallback((style: ThemeStyle) => {
    setThemeState(prev => {
      const meta = THEMES.find((t) => t.id === prev)
      const currentlyDark = meta
        ? meta.dark
        : prev === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches

      const wantDiary = style === 'diary'
      // Already in the requested style: keep the specific theme rather than
      // snapping a Forest user back to plain Midnight.
      if (meta && meta.diary === wantDiary) return prev

      const next =
        THEMES.find((t) => t.diary === wantDiary && t.dark === currentlyDark) ?? THEMES[0]

      localStorage.setItem(storageKey, next.id)
      return next.id
    })
  }, [storageKey])

  // Set diary handwritten font
  const setDiaryFont = useCallback((font: DiaryFontId) => {
    localStorage.setItem('horton-diary-font', font)
    setDiaryFontState(font)
  }, [])

  // Set accent color
  const setAccentColor = useCallback((color: AccentColorId) => {
    localStorage.setItem('horton-accent-color', color)
    setAccentColorState(color)
  }, [])

  // Set UI font
  const setUIFont = useCallback((font: UIFontId) => {
    localStorage.setItem('horton-ui-font', font)
    setUIFontState(font)
  }, [])

  // Yanamas customization setters
  const setYanamasButtonStyle = useCallback((style: YanamasButtonStyle) => {
    localStorage.setItem('yanamas-button-style', style)
    setYanamasButtonStyleState(style)
  }, [])
  const setYanamasCardStyle = useCallback((style: YanamasCardStyle) => {
    localStorage.setItem('yanamas-card-style', style)
    setYanamasCardStyleState(style)
  }, [])
  const setYanamasUiFont = useCallback((font: UIFontId) => {
    localStorage.setItem('yanamas-ui-font', font)
    setYanamasUiFontState(font)
  }, [])
  const setYanamasBodyFont = useCallback((font: BodyFontId) => {
    localStorage.setItem('yanamas-body-font', font)
    setYanamasBodyFontState(font)
  }, [])

  const setUserOverrideToken = useCallback((id: keyof UserThemeOverride, value: string | null) => {
    setUserOverrideState((prev) => {
      const next = { ...prev }
      if (value) next[id] = value
      else delete next[id]
      localStorage.setItem(USER_OVERRIDE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  const resetUserOverride = useCallback(() => {
    localStorage.removeItem(USER_OVERRIDE_KEY)
    setUserOverrideState({})
  }, [])

  const value: ThemeContextType = {
    theme,
    isDark,
    isDiary,
    themeStyle,
    diaryFont,
    diaryFontFamily,
    diaryThemeConfig,
    accentColor,
    uiFont,
    yanamasButtonStyle,
    yanamasCardStyle,
    yanamasUiFont,
    yanamasBodyFont,
    setTheme,
    toggleTheme,
    toggleDarkMode,
    setThemeStyle,
    setDiaryFont,
    setAccentColor,
    setUIFont,
    setYanamasButtonStyle,
    setYanamasCardStyle,
    setYanamasUiFont,
    setYanamasBodyFont,
    reloadDiaryTheme,
    userOverride,
    setUserOverrideToken,
    resetUserOverride,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
